import React, { useCallback, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';

import {
  doRequestCurrentCommunity,
  doClearCurrentCommunityError,
  doClearCurrentCommunity,
} from 'store/actionCreators/currentCommunity';
import { openModal, closeModal } from 'store/actionCreators/modal';
import {
  getCurrentCommunity,
  getCurrentCommunityLoading,
  getCurrentCommunityError,
} from 'store/selectors/currentCommunity';
import {
  getCommunities,
  getCommunitiesLoading,
  getCommunitiesLoaded,
} from 'store/selectors/communities';
import { getAuthUser } from 'store/selectors/auth';
import { ROUTES } from 'configs';
import { Loader } from 'components';
import CommunityRouter from './CommunityRouter';
import {
  SUBSCRIPTION_NOT_FOUND,
  ACCESS_DENIED,
  LIMIT_MEMBERS,
  INVALID_INVITATION_CODE,
} from 'constants/errors';
import { commonClearStorageCommunity } from 'utils/commonStorage';
import { INFO_POPUP_V2 } from '../constants/popups';
import { CommunityRoles, ModalTypes } from 'types';

const getCommunityRequestErrorCode = (error: { data?: { error?: string }; error?: string } | null) =>
  error?.data?.error || error?.error;

const CommunityRouterWrapper = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUser = useSelector(getAuthUser);
  const community = useSelector(getCurrentCommunity);
  const communities = useSelector(getCommunities);
  const isLoading = useSelector(getCurrentCommunityLoading);
  const areCommunitiesLoading = useSelector(getCommunitiesLoading);
  const areCommunitiesLoaded = useSelector(getCommunitiesLoaded);
  const error = useSelector(getCurrentCommunityError);
  const { communityId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const subscriptionOverShownForId = useRef<string | null>(null);

  const listedCommunity = communities?.find((c) => c?.id === communityId);
  const isListedSubscriptionInactive =
    listedCommunity?.isSubscriptionActive === false;
  const invitationCode = searchParams.get('invitationCode');

  const showSubscriptionOverPopup = useCallback(
    (id?: string) => {
      if (!id || subscriptionOverShownForId.current === id) {
        return;
      }
      subscriptionOverShownForId.current = id;
      dispatch(
        openModal({
          type: ModalTypes.SUBSCRIPTION_OVER_POPUP,
        }),
      );
    },
    [dispatch],
  );

  useEffect(() => {
    return () => {
      dispatch(doClearCurrentCommunity());
    };
  }, [dispatch]);

  useEffect(() => {
    if (subscriptionOverShownForId.current !== communityId) {
      subscriptionOverShownForId.current = null;
    }
  }, [communityId]);

  useEffect(() => {
    if (!areCommunitiesLoaded) {
      return;
    }
    if (!communityId && !listedCommunity) {
      commonClearStorageCommunity();
      navigate(ROUTES.PROFILE);
    }
  }, [areCommunitiesLoaded, communityId, listedCommunity, navigate]);

  useEffect(() => {
    if (!areCommunitiesLoaded || !communityId || !isListedSubscriptionInactive) {
      return;
    }
    showSubscriptionOverPopup(communityId);
  }, [
    areCommunitiesLoaded,
    communityId,
    isListedSubscriptionInactive,
    showSubscriptionOverPopup,
  ]);

  useEffect(() => {
    if (!currentUser || !communities || !communityId) {
      return;
    }
    const isUserMember = listedCommunity?.users?.find(
      (u) => u?.id === currentUser?.userId,
    );
    if (isUserMember?.communityPermission?.role !== CommunityRoles.BLOCKED) {
      return;
    }
    dispatch(
      openModal({
        type: INFO_POPUP_V2,
        data: {
          headerText: t('community.userBlockedTitle'),
          messageText: t('community.userBlockedText'),
          defaultButtonText: 'Ok',
          type: 'warning',
          withoutCloseButton: true,
          onConfirm: () => {
            dispatch(closeModal());
          },
        },
      }),
    );
    const timeoutId = window.setTimeout(() => {
      navigate(ROUTES.PROFILE);
    }, 3000);
    return () => window.clearTimeout(timeoutId);
  }, [
    communities,
    communityId,
    currentUser,
    dispatch,
    listedCommunity,
    navigate,
    t,
  ]);

  useEffect(() => {
    if (error) {
      commonClearStorageCommunity();
      return;
    }
    if (
      !currentUser ||
      !communityId ||
      community?.id === communityId ||
      !communities?.length ||
      isLoading ||
      !areCommunitiesLoaded ||
      isListedSubscriptionInactive ||
      subscriptionOverShownForId.current === communityId
    ) {
      return;
    }
    dispatch(
      doRequestCurrentCommunity({
        communityId,
        invitationCode,
        userId: currentUser.userId,
      }),
    );
  }, [
    areCommunitiesLoaded,
    communities,
    community,
    communityId,
    currentUser,
    dispatch,
    error,
    invitationCode,
    isListedSubscriptionInactive,
    isLoading,
  ]);

  useEffect(() => {
    if (!error) {
      return;
    }

    const errorCode = getCommunityRequestErrorCode(error);

    if (errorCode === ACCESS_DENIED) {
      if (communities.find((c) => c.id === communityId)) {
        navigate(ROUTES.PROFILE);
        dispatch(
          openModal({
            type: INFO_POPUP_V2,
            data: {
              headerText: t('community.userBlockedTitle'),
              messageText: t('community.userBlockedText'),
              defaultButtonText: 'Ok',
              type: 'warning',
              withoutCloseButton: true,
              onConfirm: () => {
                dispatch(closeModal());
              },
            },
          }),
        );
      } else {
        navigate(`/communities/${communityId}/landing`);
      }
      dispatch(doClearCurrentCommunityError());
      return;
    }

    if (errorCode === LIMIT_MEMBERS) {
      dispatch(doClearCurrentCommunityError());
      navigate('/profile');
      dispatch(
        openModal({
          type: INFO_POPUP_V2,
          data: {
            headerText: t('communitySettings.disableAddMemberTooltip'),
            defaultButtonText: 'Ok',
            type: 'warning',
            withoutCloseButton: true,
            onConfirm: () => {
              dispatch(closeModal());
            },
          },
        }),
      );
      return;
    }

    if (errorCode === INVALID_INVITATION_CODE) {
      dispatch(doClearCurrentCommunityError());
      navigate(`/communities/${communityId}/landing`);
      dispatch(
        openModal({
          type: INFO_POPUP_V2,
          data: {
            headerText: t('community.invitationCodeExpired'),
            defaultButtonText: 'Ok',
            type: 'warning',
            onConfirm: () => {
              dispatch(closeModal());
            },
          },
        }),
      );
      return;
    }

    if (errorCode === SUBSCRIPTION_NOT_FOUND) {
      if (communities.length > 0) {
        showSubscriptionOverPopup(communityId);
      } else {
        commonClearStorageCommunity();
        navigate(ROUTES.PROFILE);
      }
      dispatch(doClearCurrentCommunityError());
      return;
    }

    dispatch(doClearCurrentCommunityError());
    commonClearStorageCommunity();
    navigate(ROUTES.PROFILE);
  }, [
    communityId,
    communities,
    dispatch,
    error,
    navigate,
    showSubscriptionOverPopup,
    t,
  ]);

  if (isLoading || areCommunitiesLoading) {
    return <Loader type="main" />;
  }

  if (isListedSubscriptionInactive) {
    return null;
  }

  if (error) {
    return null;
  }

  return community?.id ? <CommunityRouter /> : <Loader type="main" />;
};

export default CommunityRouterWrapper;
